import { createTheme, responsiveFontSizes, type Theme, type ThemeOptions } from '@mui/material/styles';
import { palette, otaColors } from './palette';
import { typography } from './typography';

/**
 * Single source of truth for the OTA visual language.
 * All three apps (PropertyEnlist, OTA_internal, OTA public) consume this theme
 * via {@code <OtaThemeProvider>}.
 */
const baseOptions: ThemeOptions = {
  palette,
  typography,
  shape: {
    borderRadius: 10,
  },
  spacing: 8,
  shadows: [
    'none',
    '0 1px 2px rgba(15, 23, 42, 0.06)',
    '0 2px 4px rgba(15, 23, 42, 0.08)',
    '0 4px 10px rgba(15, 23, 42, 0.08)',
    '0 6px 16px rgba(15, 23, 42, 0.10)',
    '0 8px 24px rgba(15, 23, 42, 0.12)',
    '0 10px 32px rgba(15, 23, 42, 0.14)',
    '0 12px 40px rgba(15, 23, 42, 0.16)',
    '0 14px 48px rgba(15, 23, 42, 0.18)',
    '0 16px 56px rgba(15, 23, 42, 0.20)',
    '0 18px 64px rgba(15, 23, 42, 0.22)',
    '0 20px 72px rgba(15, 23, 42, 0.24)',
    '0 22px 80px rgba(15, 23, 42, 0.26)',
    '0 24px 88px rgba(15, 23, 42, 0.28)',
    '0 26px 96px rgba(15, 23, 42, 0.30)',
    '0 28px 104px rgba(15, 23, 42, 0.32)',
    '0 30px 112px rgba(15, 23, 42, 0.34)',
    '0 32px 120px rgba(15, 23, 42, 0.36)',
    '0 34px 128px rgba(15, 23, 42, 0.38)',
    '0 36px 136px rgba(15, 23, 42, 0.40)',
    '0 38px 144px rgba(15, 23, 42, 0.42)',
    '0 40px 152px rgba(15, 23, 42, 0.44)',
    '0 42px 160px rgba(15, 23, 42, 0.46)',
    '0 44px 168px rgba(15, 23, 42, 0.48)',
    '0 46px 176px rgba(15, 23, 42, 0.50)',
  ] as Theme['shadows'],
  breakpoints: {
    values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: otaColors.background.default,
          color: otaColors.text.primary,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        '*::-webkit-scrollbar': { width: 8, height: 8 },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: otaColors.border.strong,
          borderRadius: 4,
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 20px',
          fontWeight: 600,
          textTransform: 'none',
          transition: 'all 0.18s ease',
          '&:hover': { transform: 'translateY(-1px)' },
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${otaColors.primary[600]} 0%, ${otaColors.primary[500]} 100%)`,
          boxShadow: '0 4px 12px rgba(5, 60, 113, 0.25)',
          '&:hover': {
            background: `linear-gradient(135deg, ${otaColors.primary[700]} 0%, ${otaColors.primary[600]} 100%)`,
            boxShadow: '0 6px 18px rgba(5, 60, 113, 0.35)',
          },
        },
        containedSecondary: {
          background: `linear-gradient(135deg, ${otaColors.secondary[500]} 0%, ${otaColors.secondary[400]} 100%)`,
          color: '#FFFFFF',
          boxShadow: '0 4px 12px rgba(235, 111, 3, 0.30)',
          '&:hover': {
            background: `linear-gradient(135deg, ${otaColors.secondary[600]} 0%, ${otaColors.secondary[500]} 100%)`,
            boxShadow: '0 6px 18px rgba(235, 111, 3, 0.40)',
          },
        },
        outlinedPrimary: {
          borderWidth: 2,
          '&:hover': { borderWidth: 2, backgroundColor: otaColors.primary[50] },
        },
        sizeLarge: { padding: '14px 28px', fontSize: '1rem' },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 14,
          border: `1px solid ${otaColors.border.default}`,
          backgroundColor: otaColors.background.paper,
          transition: 'box-shadow 0.2s ease, transform 0.2s ease',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: 12 },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined', size: 'medium' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: '#FFFFFF',
          '& fieldset': { borderColor: otaColors.border.default },
          '&:hover fieldset': { borderColor: otaColors.primary[300] },
          '&.Mui-focused fieldset': { borderWidth: 2 },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 500, borderRadius: 8 },
        colorPrimary: {
          backgroundColor: otaColors.primary[50],
          color: otaColors.primary[700],
          // Clickable chips (e.g. Amenities/House Rules toggles) get a MUI-injected
          // `:hover` background of theme.palette.primary.main/dark, which is dark
          // enough that our (also dark) primary[700] label text became invisible —
          // the chip looked "blank" on hover. Override hover to a slightly darker
          // shade within the SAME light family so the dark text stays legible.
          '&:hover': {
            backgroundColor: otaColors.primary[100],
          },
          '&.MuiChip-clickable:hover': {
            backgroundColor: otaColors.primary[100],
          },
        },
        colorSecondary: {
          backgroundColor: otaColors.secondary[50],
          color: otaColors.secondary[700],
          '&:hover': {
            backgroundColor: otaColors.secondary[100],
          },
          '&.MuiChip-clickable:hover': {
            backgroundColor: otaColors.secondary[100],
          },
        },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'default' },
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          color: otaColors.text.primary,
          borderBottom: `1px solid ${otaColors.border.default}`,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: otaColors.background.dark,
          fontSize: '0.75rem',
          padding: '8px 12px',
          borderRadius: 6,
        },
      },
    },
  },
};

export const otaTheme: Theme = responsiveFontSizes(createTheme(baseOptions));
export type OtaTheme = typeof otaTheme;
